import { type UseQueryOptions } from '@tanstack/react-query';

import type { FieldPath, Fields } from '../../../@types/field-path.d';
import { getResourceQueryKey, ResourceType, useResourceQuery } from '../../resources';
import { type User } from '../types';

/** The fields `useMeQuery()` asks for by default (what the session and permissions need). */
export const DEFAULT_ME_FIELDS = [
	'_id',
	'name',
	'picture',
	'picture_url',
	'customer',
	'role',
	'userRole._id',
	'userRole.permissions',
	'status',
	'group',
] as const;

const USER_RESOURCE_TYPE = ResourceType.USERS;

export type UserQueryField = FieldPath<User> | keyof User;

export type UserQueryData<F extends UserQueryField = UserQueryField> = Fields<User, F>;

/** What `useMeQuery()` returns with its default fields. */
export type MeQueryData = UserQueryData<(typeof DEFAULT_ME_FIELDS)[number]>;

export type UserQueryVariables<F extends readonly UserQueryField[] = typeof DEFAULT_ME_FIELDS> = {
	fields?: F;
};

export const useMeQuery = <
	const F extends readonly UserQueryField[] = typeof DEFAULT_ME_FIELDS,
	TData = UserQueryData<F[number]>,
	TError = unknown,
>(
	variables: UserQueryVariables<F> = {},
	options?: UseQueryOptions<UserQueryData<F[number]>, TError, TData>
) => {
	const fields = variables.fields ?? DEFAULT_ME_FIELDS;

	return useResourceQuery<UserQueryData<F[number]>, UserQueryData<F[number]>, TError, TData>(
		{
			resourceId: '',
			callMethod: 'me',
			_fields: fields as unknown as string[],
		},
		{
			resourceType: USER_RESOURCE_TYPE,
			defaultFields: [...DEFAULT_ME_FIELDS],
			queryKeyPrefix: 'me',
		},
		options
	);
};

/** The key `useMeQuery` uses: built from the same variables, or a cache write misses the entry. */
export const getMeQueryKey = (fields: readonly UserQueryField[] = DEFAULT_ME_FIELDS) => {
	return getResourceQueryKey(
		{ resourceId: '', callMethod: 'me', _fields: fields as unknown as string[] },
		USER_RESOURCE_TYPE,
		'me'
	);
};
